import { Button, Card, CircularProgress, Typography } from '@mui/material';
import { FastField, Form, Formik, Field } from 'formik';
import { TextField, Select, CheckboxWithLabel } from 'formik-mui';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  organizationGet,
  organizationEdit,
  organizationDelete,
  checkVeeziAccess,
} from '../api/api';
import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import * as Yup from 'yup';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import phone from 'phone';
import { useProfile } from 'api/hooks';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useMedia } from 'react-use';
import ct from 'countries-and-timezones';
import { useSnackbar } from 'store/store';

export const OrganizationEdit = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const isDarkTheme = useMedia('(prefers-color-scheme: dark)');
  const backUrl = '/admin/organizations';
  const countries = ct.getAllCountries();
  const snackbar = useSnackbar();

  const alert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const hasPathParam = !!organizationId;

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === 'SUPER_ADMIN') ?? false;
  const isAdmin =
    (profile?.roles?.some((role) => role.type === 'ADMIN') || isSuperAdmin) ??
    false;

  const isWide = useMedia('(min-width: 600px)');

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['organization', organizationId],
    organizationGet(Number(organizationId!)),
    {
      enabled: hasPathParam,
      refetchOnWindowFocus: false,
    },
  );
  const organization: any = response?.data;

  console.log('organization', organization);

  const {
    mutateAsync,
    error: mutateError,
    data: mutateData,
    isLoading: mutateIsLoading,
  } = useMutation('organizationEdit', organizationEdit, {
    onSuccess: () => {
      queryClient.invalidateQueries('organization');
      navigate(backUrl);
    },
    onError: (err: any) => {
      alert('Error', err.message);
    },
  });

  const handlePhoneChange = useCallback(
    (originalHandler) => (value) => {
      originalHandler({ target: { name: 'phone', value } });
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2 justify-center items-center w-full">
      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`${alertTitle}`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`${alertMessage}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      {isLoading && (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {(!hasPathParam || organization) && (
        <>
          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {'Deletion confirmation'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {`This will delete the organization with all of its events, event types, venues, venue parts, ticket sets and ticket types. The oranization will not be deleted if it has a future ending event. Are you sure you want to delete all of this?`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  try {
                    const response = await organizationDelete(
                      organization.id,
                    )();
                    setConfirmOpen(false);
                    if (response) navigate(backUrl);
                  } catch (error: any) {
                    setConfirmOpen(false);
                    alert(error.error, error.message);
                  }
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Formik
            validateOnMount
            initialValues={
              organization || {
                name: '',
                legalName: '',
                email: '',
                phone: '',
                country: '',
                type: '',
                dataSource: '',
                veeziAccessToken: '',
                enabled: false,
              }
            }
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(2, 'Too short')
                .max(50, 'Too long')
                .required('Required'),
              legalName: Yup.string()
                .min(2, 'Too short')
                .max(50, 'Too long')
                .required('Required'),
              email: Yup.string().email('Invalid email').required('Required'),
              phone: Yup.string()
                .test('phone', 'Invalid phone number', (value) => {
                  console.log('phone', value);

                  if (!value) return true;
                  return phone(value).isValid;
                })
                .required('Required'),
              country: Yup.string().required('Required'),
              type: Yup.string().required('Required'),
              dataSource: Yup.string().when('type', (type?: string) =>
                type === 'cinema'
                  ? Yup.string().required('Required')
                  : Yup.string().notRequired(),
              ),
              veeziAccessToken: Yup.string().when(
                'dataSource',
                (dataSource, type) =>
                  ['VEEZI_US', 'VEEZI_EU'].includes(dataSource) &&
                  type === 'cinema'
                    ? Yup.string().min(20, 'Too short').required('Required')
                    : Yup.string().notRequired(),
              ),
            })}
            onSubmit={async ({ ...values }, { setSubmitting }) => {
              console.log(values);

              try {
                if (
                  values.type === 'cinema' &&
                  (values.dataSource === 'VEEZI_US' ||
                    values.dataSource === 'VEEZI_EU')
                ) {
                  await checkVeeziAccess({
                    veeziAccessToken: values.veeziAccessToken,
                    dataSource: values.dataSource,
                  });
                }

                const response = await mutateAsync({ values });
                if (
                  !hasPathParam &&
                  !values?.enabled &&
                  response?.status === 201
                )
                  snackbar.open({
                    type: 'success',
                    message:
                      'Thank you for your interest. We will contact you.',
                  });
              } catch (error: any) {
                alert(error.error, error.message);
              }
              setSubmitting(false);
            }}
          >
            {({
              submitForm,
              isSubmitting,
              values,
              isValid,
              handleChange,
              errors,
              touched,
              handleBlur,
            }) => (
              <Form style={{ width: '100%' }}>
                <Card
                  variant="outlined"
                  sx={{
                    width: '100%',
                    maxWidth: '450px',
                    borderWidth: isDarkTheme ? '0px' : '1px',
                    padding: { xs: '16px 16px 24px', sm: '40px' },
                    margin: '16px auto 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      textAlign: 'center',
                      mb: 2,
                      fontSize: '1.8rem',
                    }}
                  >
                    {hasPathParam ? 'Edit organization' : 'Add organization'}
                  </Typography>
                  {!hasPathParam && (
                    <>
                      <Box sx={{ color: 'red', textAlign: 'center' }}>
                        The app is in early preview.
                        <br /> All data will be deleted.
                      </Box>
                      <Box sx={{ opacity: 0.7 }}>
                        We will contact you on the provided contact information.
                        You can start adding your events after we enable your
                        organization.
                      </Box>
                    </>
                  )}
                  <FastField
                    component={TextField}
                    label="Name"
                    name="name"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Legal name"
                    name="legalName"
                    size="small"
                  />
                  <Box className="small-formik-select-fix">
                    <FastField
                      component={Select}
                      label="Country"
                      id="country"
                      name="country"
                      size="small"
                      required
                    >
                      {!!countries &&
                        Object.keys(countries).map((c) => (
                          <MenuItem
                            key={countries[c]?.id}
                            value={countries[c]?.name}
                          >
                            {countries[c]?.name}
                          </MenuItem>
                        ))}
                    </FastField>
                  </Box>
                  <FastField
                    component={TextField}
                    label="Contact email"
                    name="email"
                    type="email"
                    size="small"
                  />
                  <MuiPhoneNumber
                    required
                    defaultCountry={'rw'}
                    name="phone"
                    label="Contact phone"
                    variant="outlined"
                    value={values.phone}
                    onBlur={handleBlur}
                    onChange={handlePhoneChange(handleChange)}
                    error={touched.phone && !!errors.phone}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    helperText={(touched.phone && errors.phone) || ''}
                    className="MuiPhoneNumber"
                    InputLabelProps={{ required: false }}
                    size="small"
                  />
                  <Box className="small-formik-select-fix">
                    <FastField
                      component={Select}
                      label="Type"
                      name="type"
                      size="small"
                    >
                      <MenuItem value={'cinema'}>Cinema</MenuItem>
                      <MenuItem value={'music'}>Music</MenuItem>
                      <MenuItem value={'festival'}>Festival</MenuItem>
                      <MenuItem value={'experience'}>Experience</MenuItem>
                      <MenuItem value={'theater'}>Theater</MenuItem>
                      <MenuItem value={'child'}>Child</MenuItem>
                      <MenuItem value={'exhibition'}>Exhibition</MenuItem>
                      <MenuItem value={'sport'}>Sport</MenuItem>
                      <MenuItem value={'online'}>Online</MenuItem>
                      <MenuItem value={'variety'}>Variety</MenuItem>
                    </FastField>
                  </Box>
                  <Box className="small-formik-select-fix">
                    {values.type === 'cinema' && (
                      <FastField
                        component={Select}
                        label="Data source"
                        name="dataSource"
                        size="small"
                        required
                      >
                        <MenuItem value={'LOCAL'}>Local</MenuItem>
                        <MenuItem value={'VEEZI_EU'}>Veezi EU</MenuItem>
                        <MenuItem value={'VEEZI_US'}>Veezi US</MenuItem>
                      </FastField>
                    )}
                  </Box>
                  {(values.dataSource === 'VEEZI_EU' ||
                    values.dataSource === 'VEEZI_US') &&
                    values.type === 'cinema' && (
                      <FastField
                        component={TextField}
                        label="Veezi access token"
                        name="veeziAccessToken"
                        size="small"
                      />
                    )}
                  {isSuperAdmin && (
                    <Field
                      component={CheckboxWithLabel}
                      type="checkbox"
                      name="enabled"
                      Label={{ label: 'Enabled' }}
                      size="small"
                    />
                  )}
                  <Box
                    sx={{
                      textAlign: 'right',
                      margin: '8px 0 0',
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                  >
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      onClick={() => {
                        navigate(backUrl);
                      }}
                      sx={{ padding: '6px 8px' }}
                    >
                      Cancel
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {hasPathParam && (
                      <Button
                        color="primary"
                        disabled={isSubmitting}
                        onClick={() => {
                          setConfirmOpen(true);
                        }}
                        sx={{ padding: '6px 8px' }}
                      >
                        Delete
                      </Button>
                    )}
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={isSubmitting || !isValid}
                      onClick={submitForm}
                      sx={{ padding: '6px 24px' }}
                    >
                      Save
                    </Button>
                  </Box>
                </Card>
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
};
